import { useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useDrupalSettings from '../useDrupalSettings';
import { setTealiumStatus } from '../../slices/pdp';

import { calculateSubtotal, getProductAttributes } from '../../utils';
import {
  WARRANTY_CUSTOM_LINE_ITEM_TYPE,
  TEALIUM_ADDED_LOCATION_COOKIE,
  FREE_GIFT_AOVS,
  COMPLETE_SET_AOVS,
} from '../../utils/common/constants';
import { setTealiumShippingMethodStatus } from '../../slices/shippingMethods';
import { TEALIUM_DELIVERY_STEP, TEALIUM_PAYMENT_STEP, TEALIUM_SHIPPING_STEP, TEALIUM_VERIFY_ADDRESS_STEP } from '../../blocks/Checkout/SteppedCheckout/constants';

const useTealium = (args) => {
  const {
    isCompleted: checkoutStepIsCompleted,
    isEditing: checkoutStepIsEditing,
    isCurrent: checkoutStepIsCurrent,
    title: checkoutStep,
    handleAddToCartClicked,
  } = args || {};
  const { aovs: allAovs } = useDrupalSettings() || {};

  const { status, tealiumStatus, selectedSKU } = useSelector(
    (state) => state.pdp || {},
  );
  const {
    lineItems,
    customLineItems,
    customerId,
  } = useSelector(
    (state) => state.cart || {},
  );

  const { tealiumShippingMethodStatus, status: shippingMethodStatus } =
    useSelector((state) => state.shippingMethods || {});

  const { billingAddressDraft } = useSelector(
    (state) => state.billingAddress || {},
  );

  const lineItemsAsJSON = JSON.stringify(lineItems);
  const allAovsAsJSON = JSON.stringify(allAovs);

  const dispatch = useDispatch();

  const selectedShippingMethods = customLineItems?.filter(
    (customLineItem) =>
      customLineItem?.custom?.fields?.netsuite_shipping_name &&
      customLineItem?.custom?.fields?.lineItemsIds.length > 0,
  );
  const getInitialUtagData = () => ({
    price: [],
    originalPrice: [],
    productDiscount: [],
    parentCategory: [],
    subCategory: [],
    productName: [],
    productId: [],
    productBrand: [],
    productColor: [],
    productSize: [],
    productVariant: [],
    productQuantity: [],
    productHeight: [],
    productVariantExtraAttributes: [],
    itemListId: [],
    itemListName: [],
    itemAddedLocation: [],
    producItemListId: [],
    productItemListName: [],
  });
  const includedList = ['gender', 'pillow-height', 'feel'];
  const sizeIncludedList = [
    'size',
    'bedding-size',
    'pillow-size',
    'apparel-size',
  ];
  const colorIncludedList = ['color', 'bedframe-color'];

  const waitForUtag = (callback1, callback2) => {
    const maxAttempts = 20; // Set a maximum number of attempts
    let attempts = 0; // Track the number of checks

    const interval = setInterval(() => {
      if (
        typeof window.utag !== 'undefined' &&
        typeof window.utag.view === 'function'
      ) {
        clearInterval(interval);
        callback1();
        if (callback2 instanceof Function) {
          callback2();
        }
      } else if (attempts >= maxAttempts) {
        clearInterval(interval);
        // eslint-disable-next-line no-console
        console.error('Timed out waiting for utag.js');
      }

      attempts += 1;
    }, 500); // Check every half second
  };

  const lineItemsForUtag = useCallback((eventName, pageName, pageType) => {
    let lineItemUtagData = getInitialUtagData();
    const lineItemsParsed = JSON.parse(lineItemsAsJSON);
    const subtotal = lineItemsParsed?.length
      ? calculateSubtotal({ lineItems: lineItemsParsed })
      : 0;

    const warrantyItems = lineItemsParsed?.length
      ? customLineItems.filter(
          (customLineItem) =>
            customLineItem.custom?.fields?.type ===
            WARRANTY_CUSTOM_LINE_ITEM_TYPE,
        )
      : null;

    const totalWarranties = warrantyItems
      ? calculateSubtotal({ lineItems: warrantyItems })
      : 0;

    let promoCodes = null;
    lineItemsParsed?.forEach((lineItem) => {
      // fetch cart item added location from localstorage
      const addedLocation = JSON.parse(
        localStorage.getItem(TEALIUM_ADDED_LOCATION_COOKIE),
      );
      const lineItemLocation = addedLocation?.find(
        (location) => location.lineItemId === lineItem.variant.sku,
      );
      const {
        parentCategory,
        subCategory,
        variantColor,
        variantSize,
        productVariantExtraAttributes,
      } = getProductAttributes(lineItem);
      const discountJson = lineItem?.custom?.fields?.discounts_json;
      const itemDiscount =
        discountJson &&
        JSON.parse(discountJson)?.reduce(
          (totalLine, discount) => totalLine + discount.cent_amount,
          0,
        );
      const couponCodes =
        discountJson &&
        JSON.parse(discountJson)?.reduce(
          (allCoupons, discount) =>
            discount.coupon_code
              ? [...allCoupons, discount.coupon_code]
              : allCoupons,
          [],
        );
      promoCodes = couponCodes.length ? couponCodes : null;
      const productOriginalPrice =
        ((lineItem?.price?.value?.centAmount + itemDiscount) *
          lineItem.quantity) /
        100;

      lineItemUtagData = {
        price: [
          ...lineItemUtagData.price,
          (lineItem?.price?.value?.centAmount * lineItem.quantity) / 100,
        ],
        originalPrice: [
          ...lineItemUtagData.originalPrice,
          productOriginalPrice,
        ],
        parentCategory: [...lineItemUtagData.parentCategory, parentCategory],
        subCategory: [...lineItemUtagData.subCategory, subCategory],
        productName: [...lineItemUtagData.productName, lineItem.name['en-US']],
        productId: [...lineItemUtagData.productId, lineItem.variant.sku],
        productBrand: [...lineItemUtagData.productBrand, 'Purple'],
        productColor: [...lineItemUtagData.productColor, variantColor],
        productSize: [...lineItemUtagData.productSize, variantSize],
        productVariant: [
          ...lineItemUtagData.productVariant,
          productVariantExtraAttributes,
        ],
        productQuantity: [
          ...lineItemUtagData.productQuantity,
          lineItem.quantity,
        ],
        productDiscount: [...lineItemUtagData.productDiscount, itemDiscount],
        itemListId: [
          ...lineItemUtagData.itemListId,
          lineItemLocation?.item_list_id,
        ],
        itemListName: [
          ...lineItemUtagData.itemListName,
          lineItemLocation?.item_list_id
            ? lineItemLocation?.item_list_id
                .replace(/_/g, ' ')
                .replace(/\b\w/g, (char) => char.toUpperCase())
            : null,
        ],
        itemAddedLocation: [
          ...lineItemUtagData.itemAddedLocation,
          lineItemLocation?.item_added_location,
        ],
        producItemListId: [
          ...lineItemUtagData.producItemListId,
          lineItemLocation?.item_list_id,
        ],
        productItemListName: [
          ...lineItemUtagData.productItemListName,
          lineItemLocation?.item_list_id
            ? lineItemLocation?.item_list_id
                .replace(/_/g, ' ')
                .replace(/\b\w/g, (char) => char.toUpperCase())
            : null,
        ],
      };
    });
    const cartValue = (subtotal + totalWarranties) / 100;
    if (checkoutStepIsCurrent || pageType === 'Cart') {
      if (warrantyItems) {
        warrantyItems.forEach((warranty) => {
          const {
            custom: { fields: selectedWarrantyPlan },
          } = warranty;
          const { warrantyPlanId, warrantyPrice, warrantyTerm, lineItemsIds } =
            selectedWarrantyPlan;
          const warrantyDurationInYears = warrantyTerm / 12;
          const totalWarrantPrice = (warrantyPrice * warranty.quantity) / 100;

          lineItemUtagData = {
            price: [...lineItemUtagData.price, totalWarrantPrice],
            originalPrice: [
              ...lineItemUtagData.originalPrice,
              totalWarrantPrice,
            ],
            parentCategory: [
              ...lineItemUtagData.parentCategory,
              'Extend Protection',
            ],
            subCategory: [
              ...lineItemUtagData.subCategory,
              `${
                lineItemsParsed.find((item) => item.id === lineItemsIds[0])
                  ?.name['en-US']
              } Protection`,
            ],
            productName: [
              ...lineItemUtagData.productName,
              `Extend Protection ${warrantyDurationInYears}Y`,
            ],
            productId: [...lineItemUtagData.productId, warrantyPlanId],
            productBrand: [...lineItemUtagData.productBrand, 'Extend'],
            productColor: [...lineItemUtagData.productColor, null],
            productSize: [...lineItemUtagData.productSize, null],
            productVariant: [...lineItemUtagData.productVariant, null],
            productQuantity: [
              ...lineItemUtagData.productQuantity,
              warranty.quantity,
            ],
            productDiscount: [...lineItemUtagData.productDiscount, 0],
          };
        });
      }
      window?.utag?.view({
        event_name: eventName,
        page_name: pageName,
        page_title: document.title,
        page_type: pageType,
        cart_value: cartValue,
        ...(promoCodes ? { promo_code: promoCodes } : {}),
        ...(cartValue > 0
          ? {
              item_list_id: lineItemUtagData.itemListId,
              item_list_name: lineItemUtagData.itemListName,
              item_added_location: lineItemUtagData.itemAddedLocation,
              product_item_list_id: lineItemUtagData.producItemListId,
              product_item_list_name: lineItemUtagData.productItemListName,
              product_id: lineItemUtagData.productId,
              product_name: lineItemUtagData.productName,
              product_brand: lineItemUtagData.productBrand,
              product_quantity: lineItemUtagData.productQuantity,
              product_price: lineItemUtagData.price,
              product_original_price: lineItemUtagData.originalPrice,
              product_color: lineItemUtagData.productColor,
              product_size: lineItemUtagData.productSize,
              product_variant: lineItemUtagData.productVariant,
              product_discount_value: lineItemUtagData.productDiscount.map(d =>
                d !== null && d !== '' && !Number.isNaN(Number(d))
                  ? Math.floor(Number(d))
                  : 0
              ),
              product_category: lineItemUtagData.parentCategory,
              product_sub_category: lineItemUtagData.subCategory,
            }
          : {}),
      });
    }
  }, [checkoutStepIsCurrent, customLineItems, lineItemsAsJSON]);

  const findAvailableVariants = (products) =>
    products.reduce((selectedVariants, product) => {
      // Find the master variant or fallback to the first variant.
      const variants = product?.variants || product?.gift?.variants;
      let variant = variants.find((v) => v.masterVariant) || variants[0];

      // Check if the selected variant is out of stock.
      // If it is, find the first variant that is not out of stock.
      if (variant.isOutOfStock) {
        const availableVariant = variants.find((v) => !v.isOutOfStock);
        // If there's an available variant, use it. Otherwise, stick with the original selection.
        variant = availableVariant || variant;
      }

      if (product?.gift) {
        // Update the nested 'gift.variants' array for the current product
        const updatedProduct = {
          ...product,
          gift: {
            ...product.gift,
            variants: [variant], // Assuming 'variant' is defined within the scope
          },
        };
        return [...selectedVariants, updatedProduct];
      }
        // Add the product and the selected variant to the accumulator.
        return [...selectedVariants, { ...product, variants: [variant] }];
    }, []);
    

  const getBundleVariantOption = (products) =>
    products
      .map(
        (product) =>
          product.options
            .map((option) => {
              const masterVariant = option.variants.find(
                (variant) =>
                  variant.masterVariant &&
                  variant.sku !== selectedSKU &&
                  !variant.isOutOfStock,
              );

              if (masterVariant) {
                return { ...option, variants: [masterVariant] }; // Return option with only the master variant
              }

              const firstAvailableVariant = option.variants.find(
                (variant) => !variant.isOutOfStock,
              );

              if (firstAvailableVariant) {
                return { ...option, variants: [firstAvailableVariant] }; // Return option with the first available variant
              }

              // If no suitable master variant or first available variant, find the first available one
              const anyAvailableVariant = option.variants.find(
                (variant) => !variant.isOutOfStock,
              );
              if (anyAvailableVariant) {
                return { ...option, variants: [anyAvailableVariant] }; // Return option with any available variant
              }

              return null; // No variants are suitable
            })
            .filter((option) => option !== null), // Filter out null entries where no suitable variant was found
      )
      .flat(); // Flatten the resulting array of arrays

  const bundleAovUtag = () => {
    const bundles = allAovs?.find(
      (aov) =>
        aov.targetSku === selectedSKU &&
        (aov.bundles?.showOnPdp || aov.bundles?.showOnPdpV2),
    )?.bundles;
    const bundleItems = bundles?.items
      .map((item) => ({
        ...item, // Spread the product to keep its structure.
        options: item.options.filter((option) =>
          option.variants.every((variant) => variant.sku !== selectedSKU),
        ),
      }))
      // Filter out products that have no matching options after the filtering.
      .filter((product) => product.options.length > 0);

    if (bundleItems?.length) {
      let bundleItemUtagData = getInitialUtagData();
      const aovProducts = getBundleVariantOption(bundleItems);
      aovProducts.forEach((aovProduct) => {
        const {
          attributes,
          price: { centAmount: price },
          discount: { centAmount: discountCentAmount },
          sku,
        } = aovProduct?.variants[0];
        const variantColor =
          attributes.find((attr) => colorIncludedList.includes(attr.name))
            ?.label || '';
        const variantSize =
          attributes.find((attr) => sizeIncludedList.includes(attr.name))
            ?.label || '';

        const productVariantExtraAttributesArray = attributes.filter((attr) =>
          includedList.includes(attr.name),
        )[0];
        const productVariantExtraAttributes =
          productVariantExtraAttributesArray?.value?.label ||
          (productVariantExtraAttributesArray?.label ?? null);

        const productPrice = price / 100;
        const discount = discountCentAmount / 100;
        const originalPrice = discount ? productPrice + discount : productPrice;

        const parentCategory = aovProduct?.category?.[1]?.name || null;
        const subCategory = aovProduct?.category?.[2]?.name || null;

        bundleItemUtagData = {
          price: [...bundleItemUtagData.price, productPrice],
          originalPrice: [...bundleItemUtagData.originalPrice, originalPrice],
          parentCategory: [
            ...bundleItemUtagData.parentCategory,
            parentCategory,
          ],
          subCategory: [...bundleItemUtagData.subCategory, subCategory],
          productName: [
            ...bundleItemUtagData.productName,
            aovProduct.productName['en-US'],
          ],
          productId: [...bundleItemUtagData.productId, sku],
          productBrand: [...bundleItemUtagData.productBrand, 'Purple'],
          productColor: [...bundleItemUtagData.productColor, variantColor],
          productSize: [...bundleItemUtagData.productSize, variantSize],
          productVariant: [
            ...bundleItemUtagData.productVariant,
            productVariantExtraAttributes,
          ],
          productQuantity: [...bundleItemUtagData.productQuantity, 1],
          productDiscount: [...bundleItemUtagData.productDiscount, discount],
          producItemListId: [...bundleItemUtagData.producItemListId, 'product_page_aov_drivers'],
          productItemListName: [...bundleItemUtagData.productItemListName, 'Product Page AOV Drivers'],
        };
      });
      window.utag.link({
        event_name: 'view product list',
        item_list_id: 'product_page_aov_drivers',
        item_list_name: 'Product Page AOV Drivers',
        item_list_location: `${window.drupalSettings.product.name} aov bundles`,
        product_item_list_id: bundleItemUtagData.producItemListId,
        product_item_list_name: bundleItemUtagData.productItemListName,
        product_id: bundleItemUtagData.productId,
        product_name: bundleItemUtagData.productName,
        product_brand: bundleItemUtagData.productBrand,
        product_category: bundleItemUtagData.parentCategory,
        product_sub_category: bundleItemUtagData.subCategory,
        product_quantity: bundleItemUtagData.productQuantity,
        product_price: bundleItemUtagData.price,
        product_original_price: bundleItemUtagData.originalPrice,
        product_color: bundleItemUtagData.productColor,
        product_size: bundleItemUtagData.productSize,
        product_variant: bundleItemUtagData.productVariant,
        product_discount_value: bundleItemUtagData.productDiscount.map(d =>
          d !== null && d !== '' && !Number.isNaN(Number(d))
            ? Math.floor(Number(d))
            : 0
        ),
      });
    }
  };

  const frameAovsUtag = () => {
    const targetSkuAOVs = allAovs?.filter(
      (aov) => aov.targetSku === selectedSKU,
    )[0]?.frames?.options;

    if (targetSkuAOVs) {
      const framesAOVs = findAvailableVariants(targetSkuAOVs);
      let framesAOVsUtagData = getInitialUtagData();
      if (framesAOVs?.length) {
        framesAOVs.forEach((aov) => {
          const {
            attributes,
            price: { centAmount: price },
            discount: { centAmount: discountCentAmount },
            sku,
          } = aov.variants[0];
          const variantColor =
            attributes.find((attr) => attr.name === 'color')?.label || '';
          const variantSize =
            attributes.find((attr) => attr.name === 'size')?.label || '';

          const productVariantExtraAttributesArray = attributes.filter((attr) =>
            includedList.includes(attr.name),
          );
          const productVariantExtraAttributes =
            productVariantExtraAttributesArray?.[0]?.value?.label || null;
          const productPrice = price / 100;
          const discount = discountCentAmount / 100;
          const originalPrice = discount
            ? productPrice + discount
            : productPrice;

          const parentCategory = aov?.category?.[1]?.name || null;
          const subCategory = aov?.category?.[2]?.name || null;

          framesAOVsUtagData = {
            price: [...framesAOVsUtagData.price, productPrice],
            originalPrice: [...framesAOVsUtagData.originalPrice, originalPrice],
            parentCategory: [
              ...framesAOVsUtagData.parentCategory,
              parentCategory,
            ],
            subCategory: [...framesAOVsUtagData.subCategory, subCategory],
            productName: [
              ...framesAOVsUtagData.productName,
              aov.productName['en-US'],
            ],
            productId: [...framesAOVsUtagData.productId, sku],
            productBrand: [...framesAOVsUtagData.productBrand, 'Purple'],
            productColor: [...framesAOVsUtagData.productColor, variantColor],
            productSize: [...framesAOVsUtagData.productSize, variantSize],
            productVariant: [
              ...framesAOVsUtagData.productVariant,
              productVariantExtraAttributes,
            ],
            productQuantity: [...framesAOVsUtagData.productQuantity, 1],
            productDiscount: [...framesAOVsUtagData.productDiscount, discount],
            producItemListId: [
              ...framesAOVsUtagData.producItemListId,
              'product_page_aov_drivers',
            ],
            productItemListName: [
              ...framesAOVsUtagData.productItemListName,
              'Product Page AOV Drivers',
            ],
          };
        });
      }
      window.utag.link({
        event_name: 'view product list',
        item_list_id: 'product_page_aov_drivers',
        item_list_name: 'Product Page AOV Drivers',
        item_list_location: `${window.drupalSettings.product.name} bed frames aovs`,
        product_item_list_id: framesAOVsUtagData.producItemListId,
        product_item_list_name: framesAOVsUtagData.productItemListName,
        product_id: framesAOVsUtagData.productId,
        product_name: framesAOVsUtagData.productName,
        product_brand: framesAOVsUtagData.productBrand,
        product_category: framesAOVsUtagData.parentCategory,
        product_sub_category: framesAOVsUtagData.subCategory,
        product_quantity: framesAOVsUtagData.productQuantity,
        product_price: framesAOVsUtagData.price,
        product_original_price: framesAOVsUtagData.originalPrice,
        product_color: framesAOVsUtagData.productColor,
        product_size: framesAOVsUtagData.productSize,
        product_variant: framesAOVsUtagData.productVariant,
        product_discount_value: framesAOVsUtagData.productDiscount.map(d =>
          d !== null && d !== '' && !Number.isNaN(Number(d))
            ? Math.floor(Number(d))
            : 0
        ),
      });
    }
  };

  const fixedAovsUtagForMiniCart = useCallback(() => {
    const includedAttrList = ['gender', 'pillow-height', 'feel'];
    const allAovsParsed = JSON.parse(allAovsAsJSON);
    const targetSkuAOVs = allAovsParsed?.filter(
      (aov) => aov.targetSku === selectedSKU,
    )[0]?.flatRate;

    if (targetSkuAOVs) {
      const flatRatesAOVs = findAvailableVariants(targetSkuAOVs.items);
      let fixedAOVsUtagData = getInitialUtagData();
      if (flatRatesAOVs?.length) {
        flatRatesAOVs.forEach((aov) => {
          const {
            attributes,
            price: { centAmount: price },
            discount: { centAmount: discountCentAmount },
            sku,
          } = aov.variants[0];
          const variantColor =
            attributes.find((attr) => attr.name === 'color')?.label || '';
          const variantSize =
            attributes.find((attr) => attr.name === 'size')?.label || '';

          const productVariantExtraAttributesArray = attributes.filter((attr) =>
            includedAttrList.includes(attr.name),
          );
          const productVariantExtraAttributes =
            productVariantExtraAttributesArray?.[0]?.value?.label || null;

          const productPrice = price / 100;
          const discount = discountCentAmount / 100;
          const originalPrice = discount
            ? productPrice + discount
            : productPrice;

          const parentCategory = aov?.category?.[1]?.name || null;
          const subCategory = aov?.category?.[2]?.name || null;

          fixedAOVsUtagData = {
            price: [...fixedAOVsUtagData.price, productPrice],
            originalPrice: [...fixedAOVsUtagData.originalPrice, originalPrice],
            parentCategory: [
              ...fixedAOVsUtagData.parentCategory,
              parentCategory,
            ],
            subCategory: [...fixedAOVsUtagData.subCategory, subCategory],
            productName: [
              ...fixedAOVsUtagData.productName,
              aov.productName['en-US'],
            ],
            productId: [...fixedAOVsUtagData.productId, sku],
            productBrand: [...fixedAOVsUtagData.productBrand, 'Purple'],
            productColor: [...fixedAOVsUtagData.productColor, variantColor],
            productSize: [...fixedAOVsUtagData.productSize, variantSize],
            productVariant: [
              ...fixedAOVsUtagData.productVariant,
              productVariantExtraAttributes,
            ],
            productQuantity: [...fixedAOVsUtagData.productQuantity, 1],
            productDiscount: [...fixedAOVsUtagData.productDiscount, discount],
            producItemListId: [
              ...fixedAOVsUtagData.producItemListId,
              'mini_cart_aov_drivers',
            ],
            productItemListName: [
              ...fixedAOVsUtagData.productItemListName,
              'Product Page mini cart AOV',
            ],
          };
        });
      }
      window.utag.link({
        event_name: 'view mini cart',
        item_list_id: 'mini_cart_aov_drivers',
        item_list_name: 'Product Page mini cart AOV',
        product_item_list_id: fixedAOVsUtagData.producItemListId,
        product_item_list_name: fixedAOVsUtagData.productItemListName,
        product_id: fixedAOVsUtagData.productId,
        product_name: fixedAOVsUtagData.productName,
        product_brand: fixedAOVsUtagData.productBrand,
        product_category: fixedAOVsUtagData.parentCategory,
        product_sub_category: fixedAOVsUtagData.subCategory,
        product_quantity: fixedAOVsUtagData.productQuantity,
        product_price: fixedAOVsUtagData.price,
        product_original_price: fixedAOVsUtagData.originalPrice,
        product_discount_value: fixedAOVsUtagData.productDiscount.map(d =>
          d !== null && d !== '' && !Number.isNaN(Number(d))
            ? Math.floor(Number(d))
            : 0
        ),
        product_color: fixedAOVsUtagData.productColor,
        product_size: fixedAOVsUtagData.productSize,
        product_variant: fixedAOVsUtagData.productVariant,
      });
    }
  }, [allAovsAsJSON, selectedSKU]);

  const freeGiftsAovsUtagForMiniCart = useCallback(() => {
    const includedAttrList = ['gender', 'pillow-height', 'feel'];
    const allAovsParsed = JSON.parse(allAovsAsJSON);
      
    const processFreeGiftAovs = (AOVsToBeProcessed, aovType) => {
      let fixedAOVsUtagData = getInitialUtagData();
      if (AOVsToBeProcessed?.length) {
        AOVsToBeProcessed.forEach((aov) => {
          const selectedAOV = aov?.gift ? aov?.gift : aov;
          const {
            attributes,
            price: { centAmount: price },
            discount: { centAmount: discountCentAmount } = {},
            discountsTier: [
              { discount: { centAmount: discountTierCentAmount } = {} } = {},
            ] = [],
            sku,
          } = selectedAOV.variants[0];
          
          const productDiscount = (discountCentAmount || discountTierCentAmount);

          const variantColor =
            attributes.find((attr) => attr.name === 'color')?.label || '';
          const variantSize =
            attributes.find((attr) => attr.name === 'size')?.label || '';

          const productVariantExtraAttributesArray = attributes.filter((attr) =>
            includedAttrList.includes(attr.name),
          );
          const productVariantExtraAttributes =
            productVariantExtraAttributesArray?.[0]?.value?.label || null;

          const productPrice =
            (aovType === FREE_GIFT_AOVS ? 0 : price / 100) *
            (selectedAOV?.maxQuantity || 1);
          const discount = productDiscount
            ? (productDiscount / 100) * (selectedAOV?.maxQuantity || 1)
            : 0;
          const originalPrice =
            (discount ? productPrice + discount : productPrice);

          const parentCategory = selectedAOV?.category?.[1]?.name || null;
          const subCategory = selectedAOV?.category?.[2]?.name || null;

          fixedAOVsUtagData = {
            price: [...fixedAOVsUtagData.price, productPrice],
            originalPrice: [...fixedAOVsUtagData.originalPrice, originalPrice],
            parentCategory: [
              ...fixedAOVsUtagData.parentCategory,
              parentCategory,
            ],
            subCategory: [...fixedAOVsUtagData.subCategory, subCategory],
            productName: [
              ...fixedAOVsUtagData.productName,
              selectedAOV.productName['en-US'],
            ],
            productId: [...fixedAOVsUtagData.productId, sku],
            productBrand: [...fixedAOVsUtagData.productBrand, 'Purple'],
            productColor: [...fixedAOVsUtagData.productColor, variantColor],
            productSize: [...fixedAOVsUtagData.productSize, variantSize],
            productVariant: [
              ...fixedAOVsUtagData.productVariant,
              productVariantExtraAttributes,
            ],
            productQuantity: [
              ...fixedAOVsUtagData.productQuantity,
              selectedAOV?.maxQuantity || 1,
            ],
            productDiscount: [...fixedAOVsUtagData.productDiscount, discount],
            producItemListId: [
              ...fixedAOVsUtagData.producItemListId,
              'mini_cart_aov_drivers',
            ],
            productItemListName: [
              ...fixedAOVsUtagData.productItemListName,
              'Product Page mini cart AOV',
            ],
          };
        });
      }
      window.utag.link({
        event_name: `view mini cart - ${aovType} AOVs`,
        item_list_id: 'mini_cart_aov_drivers',
        item_list_name: 'Product Page mini cart AOV',
        product_item_list_id: fixedAOVsUtagData.producItemListId,
        product_item_list_name: fixedAOVsUtagData.productItemListName,
        product_id: fixedAOVsUtagData.productId,
        product_name: fixedAOVsUtagData.productName,
        product_brand: fixedAOVsUtagData.productBrand,
        product_category: fixedAOVsUtagData.parentCategory,
        product_sub_category: fixedAOVsUtagData.subCategory,
        product_quantity: fixedAOVsUtagData.productQuantity,
        product_price: fixedAOVsUtagData.price,
        product_original_price: fixedAOVsUtagData.originalPrice,
        product_discount_value: fixedAOVsUtagData.productDiscount.map(d =>
          d !== null && d !== '' && !Number.isNaN(Number(d))
            ? Math.floor(Number(d))
            : 0
        ),
        product_color: fixedAOVsUtagData.productColor,
        product_size: fixedAOVsUtagData.productSize,
        product_variant: fixedAOVsUtagData.productVariant,
      });
    };

    const targetSkuAOVs = allAovsParsed?.filter(
      (aov) => aov.targetSku === selectedSKU,
    )[0]?.gifts;

    const targetSkuCompleteSetAOVs = allAovsParsed?.filter(
      (aov) => aov.targetSku === selectedSKU,
    )[0]?.gifts?.completeSet;

    const bundleFreeGiftsAOVs = allAovsParsed?.filter(
      (aov) => aov.targetSku === selectedSKU,
    )[0]?.bundleGifts;

    if (bundleFreeGiftsAOVs) {
      const freeGiftAOVs = findAvailableVariants(bundleFreeGiftsAOVs?.options);
      processFreeGiftAovs(freeGiftAOVs, FREE_GIFT_AOVS);
    }
    
    if (targetSkuAOVs && !bundleFreeGiftsAOVs) {
      const freeGiftAOVs = findAvailableVariants([
        targetSkuAOVs?.options?.[0]?.gift,
      ]);
      processFreeGiftAovs(freeGiftAOVs, FREE_GIFT_AOVS);
    }

    if (targetSkuCompleteSetAOVs?.length && !bundleFreeGiftsAOVs) {
      const completeTheSetAOVs = findAvailableVariants(
        targetSkuCompleteSetAOVs,
      );
      processFreeGiftAovs(completeTheSetAOVs, COMPLETE_SET_AOVS);
    }
    
  }, [allAovsAsJSON, selectedSKU]);

  // PDP bundles
  useEffect(() => {
    if (status === 'success' && !tealiumStatus) {
      waitForUtag(frameAovsUtag, bundleAovUtag);
      dispatch(setTealiumStatus({ tealiumStatus: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // mini cart
  useEffect(() => {
    if (handleAddToCartClicked) {
      waitForUtag(fixedAovsUtagForMiniCart);
      waitForUtag(freeGiftsAovsUtagForMiniCart);
      dispatch(setTealiumStatus({ tealiumStatus: true }));
    }

  }, [
    dispatch,
    fixedAovsUtagForMiniCart,
    freeGiftsAovsUtagForMiniCart,
    handleAddToCartClicked,
  ]);

  // for begin checkout page
  useEffect(() => {
    const viewUtagsForBeginCheckout = () => {
      lineItemsForUtag('Begin - checkout', 'Checkout', 'Checkout');
    };
    if (window.location.pathname === '/checkout') {
      waitForUtag(viewUtagsForBeginCheckout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // for checkout page steps - shipping info
  useEffect(() => {
    const viewUtagsForshippingInfo = () => {
      window.utag.link({
        event_name: checkoutStep,
      });
      dispatch(setTealiumShippingMethodStatus(false));
    };
    if (
      checkoutStep === TEALIUM_SHIPPING_STEP &&
      ((checkoutStepIsCompleted === false &&
        checkoutStepIsCurrent &&
        billingAddressDraft?.firstName === '') ||
        (checkoutStepIsCompleted &&
          checkoutStepIsCurrent &&
          checkoutStepIsEditing))
    ) {
      waitForUtag(viewUtagsForshippingInfo);
    }
  }, [
    checkoutStep,
    checkoutStepIsCompleted,
    checkoutStepIsCurrent,
    checkoutStepIsEditing,
    billingAddressDraft?.firstName,
    dispatch,
  ]);

  // for checkout page steps - delivery methods
  useEffect(() => {
    const viewUtagsForshippingMethods = () => {
      window.utag.link({
        event_name: checkoutStep,
      });
    };
    if (
      checkoutStep === TEALIUM_DELIVERY_STEP &&
      !tealiumShippingMethodStatus &&
      ['idle', 'loading'].includes(shippingMethodStatus) &&
      !customerId &&
      checkoutStepIsCurrent
    ) {
      waitForUtag(viewUtagsForshippingMethods);
      dispatch(setTealiumShippingMethodStatus(true));
    }
    if (
      checkoutStep === TEALIUM_DELIVERY_STEP &&
      tealiumShippingMethodStatus &&
      shippingMethodStatus === 'loading' &&
      checkoutStepIsCurrent === checkoutStepIsEditing
    ) {
      waitForUtag(viewUtagsForshippingMethods);
      dispatch(setTealiumShippingMethodStatus(true));
    }
  }, [
    selectedShippingMethods?.length,
    checkoutStepIsEditing,
    checkoutStep,
    customerId,
    dispatch,
    shippingMethodStatus,
    tealiumShippingMethodStatus,
    checkoutStepIsCurrent,
  ]);

  // for checkout page steps - verify shipping address
  useEffect(() => {
    const viewUtagsForshippingInfo = () => {
      window.utag.link({
        event_name: checkoutStep,
      });
      // dispatch(setTealiumStatus({ tealiumStatus: true }));
    };
    if (
      checkoutStep === TEALIUM_VERIFY_ADDRESS_STEP &&
      checkoutStepIsCurrent
    ) {
      waitForUtag(viewUtagsForshippingInfo);
    }
  }, [checkoutStep, checkoutStepIsCurrent, dispatch]);

  // for checkout page steps - payment info
  useEffect(() => {
    const viewUtagsForshippingInfo = () => {
      window.utag.link({
        event_name: checkoutStep,
      });
    };
    if (
      checkoutStep === TEALIUM_PAYMENT_STEP &&
      checkoutStepIsCurrent &&
      !tealiumStatus
    ) {
      // assuming deliverymethod utag is fired and avoid firing duplicates
      dispatch(setTealiumShippingMethodStatus(true));

      waitForUtag(viewUtagsForshippingInfo);
      dispatch(setTealiumStatus({ tealiumStatus: true }));
    }
  }, [
    checkoutStep,
    checkoutStepIsEditing,
    checkoutStepIsCurrent,
    checkoutStepIsCompleted,
    tealiumStatus,
    dispatch,
  ]);

};

export default useTealium;
